import React, { useEffect, useState, useCallback } from 'react';
import { PhotoType, ServicePhoto } from '../types';
import { deleteRecordPhoto, fetchRecordPhotos, getPhotoBinaryUrl, uploadRecordPhoto } from '../api';

interface PhotoGallerySectionProps {
  recordUuid: string;
  onToast: (msg: string, type?: 'info' | 'success' | 'error') => void;
}

const PHOTO_TYPE_LABELS: Record<PhotoType, string> = {
  INTAKE: 'Zdjęcia przy przyjęciu',
  REPAIR: 'Zdjęcia serwisowe',
  RESULT: 'Zdjęcia po naprawie',
};

const ALLOWED_MIMES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

export const PhotoGallerySection: React.FC<PhotoGallerySectionProps> = ({ recordUuid, onToast }) => {
  const [photos, setPhotos] = useState<ServicePhoto[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Upload Form State
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [photoType, setPhotoType] = useState<PhotoType>('INTAKE');
  const [isPrimary, setIsPrimary] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadAttemptKey, setUploadAttemptKey] = useState<string>('');

  // Lightbox State
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // Delete State
  const [deletingPhotoId, setDeletingPhotoId] = useState<string | null>(null);
  const [photoToDelete, setPhotoToDelete] = useState<ServicePhoto | null>(null);

  const loadPhotos = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchRecordPhotos(recordUuid);
      setPhotos(data);
    } catch (err: any) {
      setError(err.message || 'Nie udało się pobrać zdjęć');
    } finally {
      setIsLoading(false);
    }
  }, [recordUuid]);

  useEffect(() => {
    loadPhotos();
  }, [loadPhotos]);

  // Handle File Selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      if (!ALLOWED_MIMES.includes(file.type)) {
        onToast('Dozwolone są tylko pliki JPEG, PNG lub WEBP', 'error');
        return;
      }
      if (file.size > MAX_SIZE_BYTES) {
        onToast('Maksymalny rozmiar zdjęcia to 10 MB', 'error');
        return;
      }
      setSelectedFile(file);
      // Generate stable idempotency key for this upload attempt
      setUploadAttemptKey(crypto.randomUUID());
    }
  };

  const handleUploadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) {
      onToast('Wybierz plik ze zdjęciem', 'error');
      return;
    }

    setIsUploading(true);
    try {
      const key = uploadAttemptKey || crypto.randomUUID();
      await uploadRecordPhoto(recordUuid, selectedFile, photoType, isPrimary, key);
      onToast('Dodano nowe zdjęcie do CRM', 'success');
      setSelectedFile(null);
      setUploadAttemptKey('');
      setIsPrimary(false);
      await loadPhotos();
    } catch (err: any) {
      onToast(err.message || 'Błąd przesyłania zdjęcia', 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const confirmDelete = async () => {
    if (!photoToDelete) return;
    setDeletingPhotoId(photoToDelete.id);
    try {
      await deleteRecordPhoto(recordUuid, photoToDelete.id);
      onToast('Usunięto zdjęcie z Central CRM', 'success');
      setPhotoToDelete(null);
      if (lightboxIndex !== null) {
        setLightboxIndex(null);
      }
      await loadPhotos();
    } catch (err: any) {
      onToast(err.message || 'Błąd usuwania zdjęcia', 'error');
    } finally {
      setDeletingPhotoId(null);
    }
  };

  // Keyboard navigation for Lightbox
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (lightboxIndex === null) return;
      if (e.key === 'Escape') {
        setLightboxIndex(null);
      } else if (e.key === 'ArrowRight') {
        setLightboxIndex((prev) => (prev !== null && prev < photos.length - 1 ? prev + 1 : prev));
      } else if (e.key === 'ArrowLeft') {
        setLightboxIndex((prev) => (prev !== null && prev > 0 ? prev - 1 : prev));
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxIndex, photos.length]);

  return (
    <div className="card-container" style={{ marginTop: '20px' }}>
      <div className="card-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>🖼️ Galeria Zdjęć w Central CRM</span>
        <span style={{ fontSize: '0.8rem', fontWeight: 500, color: 'var(--text-muted)' }}>
          {photos.length} {photos.length === 1 ? 'zdjęcie' : 'zdjęć'}
        </span>
      </div>

      {/* Upload Form */}
      <form onSubmit={handleUploadSubmit} style={{ marginBottom: '20px', padding: '12px', background: 'var(--bg-subtle, #f8fafc)', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
        <div style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: '8px', color: 'var(--text-main)' }}>➕ Dodaj nowe zdjęcie</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 260px' }}>
            <label className="field-label" htmlFor="photo-file-upload" style={{ fontSize: '0.75rem', display: 'block', marginBottom: '4px' }}>
              Plik zdjęcia (JPEG/PNG/WEBP, max 10MB)
            </label>
            <div className="custom-file-picker-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input
                id="photo-file-upload"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleFileChange}
                disabled={isUploading}
                style={{
                  position: 'absolute',
                  width: '1px',
                  height: '1px',
                  padding: 0,
                  margin: '-1px',
                  overflow: 'hidden',
                  clip: 'rect(0, 0, 0, 0)',
                  border: 0,
                }}
              />
              <label
                htmlFor="photo-file-upload"
                className="btn btn-secondary btn-sm custom-file-picker-btn"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    document.getElementById('photo-file-upload')?.click();
                  }
                }}
                style={{
                  cursor: isUploading ? 'not-allowed' : 'pointer',
                  whiteSpace: 'nowrap',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  flexShrink: 0,
                }}
              >
                📷 Wybierz zdjęcie
              </label>
              <span
                className="custom-file-picker-filename"
                title={selectedFile ? selectedFile.name : 'Nie wybrano pliku'}
                style={{
                  fontSize: '0.825rem',
                  color: selectedFile ? 'var(--text-main)' : 'var(--text-light)',
                  fontStyle: selectedFile ? 'normal' : 'italic',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '220px',
                }}
              >
                {selectedFile ? selectedFile.name : 'Nie wybrano pliku'}
              </span>
            </div>
          </div>

          <div style={{ flex: '0 1 180px' }}>
            <label className="field-label" style={{ fontSize: '0.75rem' }}>Kategoria</label>
            <select value={photoType} onChange={(e) => setPhotoType(e.target.value as PhotoType)} className="field-input" disabled={isUploading}>
              <option value="INTAKE">{PHOTO_TYPE_LABELS.INTAKE}</option>
              <option value="REPAIR">{PHOTO_TYPE_LABELS.REPAIR}</option>
              <option value="RESULT">{PHOTO_TYPE_LABELS.RESULT}</option>
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', paddingBottom: '8px' }}>
            <input type="checkbox" id="isPrimaryCheck" checked={isPrimary} onChange={(e) => setIsPrimary(e.target.checked)} disabled={isUploading} />
            <label htmlFor="isPrimaryCheck" style={{ fontSize: '0.825rem', cursor: 'pointer' }}>Główne zdjęcie</label>
          </div>

          <button type="submit" className="btn btn-primary btn-sm" disabled={!selectedFile || isUploading}>
            {isUploading ? 'Wysyłanie...' : '📤 Wyślij do R2'}
          </button>
        </div>
      </form>

      {/* Loading state */}
      {isLoading && <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)' }}>Wczytywanie galerii zdjęć...</div>}

      {/* Error state */}
      {error && (
        <div style={{ padding: '12px', background: '#fef2f2', color: '#dc2626', borderRadius: '6px', fontSize: '0.875rem' }}>
          ⚠️ {error}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && photos.length === 0 && (
        <div style={{ padding: '30px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
          Brak zdjęć w Central CRM
        </div>
      )}

      {/* Photo Grid by Type */}
      {!isLoading && photos.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {(['INTAKE', 'REPAIR', 'RESULT'] as PhotoType[]).map((type) => {
            const groupPhotos = photos.filter((p) => p.photoType === type);
            if (groupPhotos.length === 0) return null;

            return (
              <div key={type}>
                <div style={{ fontSize: '0.825rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {PHOTO_TYPE_LABELS[type]} ({groupPhotos.length})
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '12px' }}>
                  {groupPhotos.map((photo) => {
                    const globalIdx = photos.findIndex((p) => p.id === photo.id);
                    const imgUrl = getPhotoBinaryUrl(recordUuid, photo.id);

                    return (
                      <div
                        key={photo.id}
                        style={{
                          position: 'relative',
                          aspectRatio: '1',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          border: photo.isPrimary ? '2px solid var(--primary)' : '1px solid #e2e8f0',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                          background: '#f1f5f9',
                          cursor: 'pointer',
                        }}
                        onClick={() => setLightboxIndex(globalIdx)}
                      >
                        <img
                          src={imgUrl}
                          alt={photo.fileName}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          loading="lazy"
                        />

                        {photo.isPrimary && (
                          <div
                            style={{
                              position: 'absolute',
                              top: '4px',
                              left: '4px',
                              background: 'var(--primary)',
                              color: '#fff',
                              fontSize: '0.65rem',
                              fontWeight: 700,
                              padding: '2px 6px',
                              borderRadius: '4px',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
                            }}
                          >
                            ★ Główna
                          </div>
                        )}

                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPhotoToDelete(photo);
                          }}
                          style={{
                            position: 'absolute',
                            top: '4px',
                            right: '4px',
                            background: 'rgba(239, 68, 68, 0.85)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '50%',
                            width: '24px',
                            height: '24px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.75rem',
                            cursor: 'pointer',
                          }}
                          title="Usuń zdjęcie"
                        >
                          🗑️
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {photoToDelete && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div style={{ background: '#fff', padding: '24px', borderRadius: '12px', maxWidth: '400px', width: '100%', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
            <h3 style={{ marginTop: 0, fontSize: '1.1rem' }}>Usuń zdjęcie</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
              Czy na pewno chcesz usunąć zdjęcie <strong>{photoToDelete.fileName}</strong> z Central CRM? Operacja usunie plik z Cloudflare R2.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px' }}>
              <button type="button" className="btn btn-outline" onClick={() => setPhotoToDelete(null)} disabled={deletingPhotoId !== null}>
                Anuluj
              </button>
              <button type="button" className="btn btn-danger" onClick={confirmDelete} disabled={deletingPhotoId !== null}>
                {deletingPhotoId !== null ? 'Usuwanie...' : 'Usuń zdjęcie'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox Modal View */}
      {lightboxIndex !== null && photos[lightboxIndex] && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.88)',
            zIndex: 1100,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
          }}
          onClick={() => setLightboxIndex(null)}
        >
          {/* Header Bar */}
          <div
            style={{
              position: 'absolute',
              top: '16px',
              left: '16px',
              right: '16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              color: '#fff',
              zIndex: 1110,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontSize: '0.9rem' }}>
              <strong>{PHOTO_TYPE_LABELS[photos[lightboxIndex].photoType]}</strong> ({lightboxIndex + 1} / {photos.length})
            </div>
            <button
              type="button"
              onClick={() => setLightboxIndex(null)}
              style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '1.8rem', cursor: 'pointer', lineHeight: 1 }}
            >
              ✕
            </button>
          </div>

          {/* Main Image View */}
          <div
            style={{ maxWidth: '90vw', maxHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={getPhotoBinaryUrl(recordUuid, photos[lightboxIndex].id)}
              alt={photos[lightboxIndex].fileName}
              style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain', borderRadius: '6px' }}
            />
          </div>

          {/* Navigation Controls */}
          {photos.length > 1 && (
            <div style={{ position: 'absolute', bottom: '24px', display: 'flex', gap: '20px', zIndex: 1110 }} onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={lightboxIndex === 0}
                onClick={() => setLightboxIndex((prev) => (prev !== null && prev > 0 ? prev - 1 : prev))}
              >
                ◀ Poprzednie
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={lightboxIndex === photos.length - 1}
                onClick={() => setLightboxIndex((prev) => (prev !== null && prev < photos.length - 1 ? prev + 1 : prev))}
              >
                Następne ▶
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
